import AddIcon from "@mui/icons-material/Add"
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useMemo, useState } from "react"

import {
  formatCustomTagsSaveErrorFromAxios,
  getCandidateCustomTagSuggestions,
  putCandidateCustomTags,
} from "../../../api/crm/candidateCustomTags"
import { putLeadCustomTags } from "../../../api/crm/leadCustomTags"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row from "../../../common/components/Row"
import CandidatePriceTagChip from "../components/CandidatePriceTagChip"

const MAX_TAG_LENGTH = 20
const MAX_TAGS = 5

function normalizeTagsForSave(tags) {
  const out = []
  const seen = new Set()
  for (const raw of tags) {
    const label = raw.trim()
    if (!label) continue
    const k = label.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(label)
  }
  return out
}

export default function CandidateDetailCustomTagsSection({
  candidate,
  setCandidate,
  isLoading,
  openSnackbar,
  leadMode = null,
}) {
  const axios = useAuthorizedAxiosClient()
  const [draftTags, setDraftTags] = useState([])
  const [tagInput, setTagInput] = useState("")
  const [tagSuggestions, setTagSuggestions] = useState([])
  const [saving, setSaving] = useState(false)
  const [saveErrorLines, setSaveErrorLines] = useState(null)

  const refreshTagSuggestions = useCallback(async () => {
    try {
      const { data } = await getCandidateCustomTagSuggestions(axios)
      setTagSuggestions(data.tags ?? [])
    } catch {
      /* suggestions are optional; keep existing list on failure */
    }
  }, [axios])

  useEffect(() => {
    if (isLoading) return
    if (!candidate.id && !leadMode) return
    refreshTagSuggestions()
  }, [candidate.id, leadMode, isLoading, refreshTagSuggestions])

  function clearSaveError() {
    setSaveErrorLines(null)
  }

  const serverTagsKey = useMemo(
    () => JSON.stringify(candidate.custom_tags ?? []),
    [candidate.custom_tags]
  )

  useEffect(() => {
    setDraftTags([...(candidate.custom_tags ?? [])])
  }, [candidate.id, serverTagsKey])

  const dirty = useMemo(() => {
    const server = candidate.custom_tags ?? []
    if (server.length !== draftTags.length) return true
    return server.some((t, i) => t !== draftTags[i])
  }, [candidate.custom_tags, draftTags])

  function tryAddTag(rawLabel = tagInput) {
    clearSaveError()
    const label = rawLabel.trim()
    if (!label) return false
    if (draftTags.length >= MAX_TAGS) {
      openSnackbar(`You can add at most ${MAX_TAGS} tags per candidate`)
      return false
    }
    if (label.length > MAX_TAG_LENGTH) {
      openSnackbar(`Each tag can be at most ${MAX_TAG_LENGTH} characters`)
      return false
    }
    const lower = label.toLowerCase()
    if (draftTags.some((t) => t.trim().toLowerCase() === lower)) {
      openSnackbar("That tag is already in the list (ignoring capitalization).")
      return false
    }
    setDraftTags((prev) => [...prev, label])
    setTagInput("")
    return true
  }

  function handleRemoveTag(index) {
    clearSaveError()
    setDraftTags((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSave() {
    clearSaveError()
    const normalized = normalizeTagsForSave(draftTags)
    setSaving(true)
    try {
      const { data } = candidate.id
        ? await putCandidateCustomTags(axios, candidate.id, normalized)
        : await putLeadCustomTags(
            axios,
            leadMode.source,
            leadMode.id,
            normalized
          )
      setCandidate((c) => ({ ...c, custom_tags: data.tags }))
      setDraftTags([...data.tags])
      await refreshTagSuggestions()
      openSnackbar("Tags saved")
    } catch (e) {
      setSaveErrorLines(formatCustomTagsSaveErrorFromAxios(e))
    } finally {
      setSaving(false)
    }
  }

  const availableSuggestions = useMemo(() => {
    const draftLower = new Set(draftTags.map((t) => t.trim().toLowerCase()))
    return tagSuggestions.filter((s) => !draftLower.has(s.trim().toLowerCase()))
  }, [draftTags, tagSuggestions])

  if (!candidate.id && !leadMode) {
    return null
  }

  if (isLoading) {
    return (
      <Stack spacing={1.5} aria-busy="true" aria-label="Loading tags">
        <Row sx={{ flexWrap: "wrap", gap: 1 }}>
          <Chip size="small" sx={{ width: 72, opacity: 0.5 }} disabled />
          <Chip size="small" sx={{ width: 88, opacity: 0.5 }} disabled />
        </Row>
        <Row sx={{ gap: 1, alignItems: "center" }}>
          <Box
            sx={{
              flex: 1,
              height: 40,
              bgcolor: "action.hover",
              borderRadius: 1,
            }}
          />
        </Row>
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Private labels for this candidate — up to {MAX_TAGS} tags,{" "}
        {MAX_TAG_LENGTH} characters each. Click a tag below to add it, or type a
        new one, then save when you are done editing.
      </Typography>
      {saveErrorLines && saveErrorLines.length > 0 && (
        <Alert severity="error" onClose={clearSaveError}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Could not save tags
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 0 }}>
            {saveErrorLines.map((line, i) => (
              <Box component="li" key={i} sx={{ mb: 0.25 }}>
                <Typography variant="body2">{line}</Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}
      {saving && (
        <Row sx={{ alignItems: "center", gap: 1 }} aria-live="polite">
          <CircularProgress size={18} thickness={5} />
          <Typography variant="body2" color="text.secondary">
            Saving tags…
          </Typography>
        </Row>
      )}
      {draftTags.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            py: 2,
            px: 2,
            textAlign: "center",
            bgcolor: "action.hover",
          }}
        >
          <LabelOutlinedIcon
            sx={{ fontSize: 32, color: "text.disabled", mb: 0.5 }}
            aria-hidden
          />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            No tags yet
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            Add tags below, then click Save tags.
          </Typography>
        </Box>
      ) : (
        <Row
          sx={{
            flexWrap: "wrap",
            gap: 1,
            opacity: saving ? 0.65 : 1,
            transition: "opacity 0.2s",
          }}
        >
          {draftTags.map((label, index) => (
            <CandidatePriceTagChip
              key={`${index}-${label}`}
              label={label}
              onDelete={saving ? undefined : () => handleRemoveTag(index)}
              disabled={saving}
            />
          ))}
        </Row>
      )}
      {availableSuggestions.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Your tags
          </Typography>
          <Row
            sx={{
              flexWrap: "wrap",
              gap: 1,
              opacity: saving ? 0.65 : 1,
            }}
            role="list"
            aria-label="Previously created tags"
          >
            {availableSuggestions.map((label) => (
              <Chip
                key={label}
                role="listitem"
                label={label}
                size="small"
                clickable={!isLoading && !saving && draftTags.length < MAX_TAGS}
                onClick={() => tryAddTag(label)}
                disabled={isLoading || saving || draftTags.length >= MAX_TAGS}
                sx={{
                  fontWeight: 600,
                  bgcolor: "#faf0ff",
                  "&:hover": {
                    bgcolor: "#faf0ff",
                  },
                }}
              />
            ))}
          </Row>
        </Stack>
      )}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
      >
        <TextField
          size="small"
          label="New tag"
          value={tagInput}
          onChange={(e) => {
            clearSaveError()
            setTagInput(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              tryAddTag()
            }
          }}
          inputProps={{ maxLength: MAX_TAG_LENGTH }}
          helperText={`${Math.min(
            tagInput.length,
            MAX_TAG_LENGTH
          )}/${MAX_TAG_LENGTH} · ${draftTags.length}/${MAX_TAGS} tags`}
          disabled={isLoading || saving || draftTags.length >= MAX_TAGS}
          fullWidth
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            "& .MuiOutlinedInput-input::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          })}
        />
        <LoadingButton
          variant="outlined"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => tryAddTag()}
          disabled={
            isLoading ||
            saving ||
            !tagInput.trim() ||
            draftTags.length >= MAX_TAGS
          }
        >
          Add
        </LoadingButton>
        <LoadingButton
          variant="contained"
          size="medium"
          onClick={handleSave}
          loading={saving}
          disabled={isLoading || saving || !dirty}
        >
          Save tags
        </LoadingButton>
      </Stack>
    </Stack>
  )
}
